<template>
  <!--
    Composant de cellule de tableau réutilisable :
      - Affiche le texte sur 1 ligne max en mode replié ( ellipse CSS
        au cap maxChars-ch ) ; multi-ligne avec wrap au caractère
        en mode déplié.
      - Ajoute un bouton ▾ / ▴ uniquement quand la cellule est
        visuellement tronquée ( détecté par ResizeObserver ) ou en
        mode déplié multi-ligne.
      - Stateless : l'état est piloté par le parent via la prop
        {@code expanded} et l'event {@code update:expanded}.

    Il distingue 2 régimes via la classe is-long ( text.length >
    maxChars ) :
      - is-long ABSENT : wrapper inline-block , taille au contenu ,
        pas de wrap forcé même en mode déplié ( pas d'effet visuel
        sur les cellules courtes en "Tout déplier" global ).
      - is-long PRÉSENT : wrapper width fixe maxChars-ch , wrap au
        caractère ( word-break: break-all ) en mode déplié pour que
        la 1ère ligne en déplié corresponde exactement à la ligne
        tronquée en replié. La largeur fixe garantit aussi que la
        colonne ne se rétracte pas via les intrinsic-widths quand
        toutes les cellules longues sont dépliées.

    Le seuil maxChars est exposé en prop ( default 80 , configurable
    par le parent ) et est utilisé à la fois pour calculer is-long
    en JS ET via la variable CSS {@code --cell-cap} pour le sizing
    visuel. Les deux restent en cohérence sans intervention manuelle.
  -->
  <span
    class="truncatable-cell"
    :class="{ 'is-expanded': expanded, 'is-long': isLong }"
    :style="{ '--cell-cap': maxChars + 'ch' }"
  >
    <span
      ref="contentEl"
      class="truncatable-cell__content"
    >
      <slot
        :text="text"
        :is-truncated="needsToggle"
      >{{ text }}</slot>
    </span>
    <button
      v-if="needsToggle"
      type="button"
      class="truncatable-cell__toggle"
      :title="toggleLabel"
      :aria-label="toggleLabel"
      :aria-pressed="expanded"
      @click.stop="onToggle"
    >
      <b-icon
        :icon="expanded ? 'chevron-up' : 'chevron-down'"
        size="is-small"
      />
    </button>
  </span>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { BIcon } from "buefy";
import i18n from "@/i18n";

export default defineComponent({
  name: "TruncatableCell",
  components: { BIcon },
  props: {
    /** Texte brut à afficher. */
    text: {
      type: String,
      required: true,
    },
    /** État d'expand contrôlé par le parent ( v-model:expanded ). */
    expanded: {
      type: Boolean,
      default: false,
    },
    /**
     * Nombre max de caractères par ligne ( pilote la largeur visuelle
     * de la cellule via la variable CSS --cell-cap ). Au-delà , la
     * cellule passe en is-long ( largeur fixe ) pour garantir que :
     *   - le wrap point en mode déplié coïncide avec le clip point
     *     en mode replié ( même 1ère ligne dans les 2 états )
     *   - la colonne ne se rétracte pas via les intrinsic widths
     *     quand toutes les cellules sont dépliées.
     * En dessous du seuil , la cellule reste en inline-block size-
     * to-content - pas de gaspillage d'espace pour les colonnes au
     * contenu court.
     * Default 80 : convention typographique 80 colonnes.
     */
    maxChars: {
      type: Number,
      default: 80,
    },
  },
  emits: {
    "update:expanded": (value: boolean) => typeof value === "boolean",
  } as unknown as { "update:expanded": (value: boolean) => boolean },
  setup(props, { emit }) {
    const contentEl = ref<HTMLElement | null>(null);

    /**
     * Vrai si le texte dépasse le seuil maxChars en absolu. La cellule
     * passe alors en mode "long" : largeur fixée à maxChars ch , wrap
     * au caractère ( word-break: break-all ) pour que la 1ère ligne
     * en déplié soit identique au texte tronqué en replié.
     */
    const isLong = computed(() => props.text.length > props.maxChars);

    /**
     * Vrai si le contenu de la cellule excède sa boite visible et
     * mérite un bouton ▾ pour être déplié / replié.
     *
     * <p><b>Mode replié</b> : on compare {@code scrollWidth} et
     * {@code clientWidth} ; si scroll > client , l'ellipse CSS clip
     * une partie du texte ⇒ le bouton est utile.
     *
     * <p><b>Mode déplié</b> : on compare {@code scrollHeight} et
     * {@code line-height} ; si le contenu fait plus d'une ligne ,
     * c'est que la cellule SERAIT tronquée si on la repliait ⇒ on
     * affiche le bouton ( pour permettre à l'utilisateur de revenir
     * en collapsed ). Sinon ( cellule courte tenant sur 1 ligne en
     * expanded comme en collapsed ) , aucun bouton : le toggle ne
     * changerait rien visuellement et serait du bruit UX ( cas typique
     * d'un Tier 3 "Tout déplier" sur des cellules courtes ).
     */
    const needsToggle = ref(false);

    const toggleLabel = computed(() =>
      props.expanded
        ? i18n.global.t("dataTable.toggle-cell-collapse")
        : i18n.global.t("dataTable.toggle-cell-expand"),
    );

    function onToggle() {
      emit("update:expanded", !props.expanded);
    }

    /**
     * Mesure le besoin réel d'un bouton selon le mode courant.
     * En collapsed : overflow horizontal. En expanded : multi-ligne.
     */
    function measureNeedsToggle() {
      const el = contentEl.value;
      if (!el) {
        needsToggle.value = false;
        return;
      }
      if (props.expanded) {
        // Multi-ligne ssi scrollHeight > 1 ligne. On garde une marge
        // de 1.5x pour absorber les arrondis ( hauteur de ligne
        // partielle , baseline metrics ) sans déclencher faux positif.
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
          // Fallback : si line-height non calculable , se baser sur
          // scrollHeight vs clientHeight ( en expanded clientHeight
          // suit le contenu , donc égalité signifie pas d'overflow ).
          needsToggle.value = el.scrollHeight > el.clientHeight + 1;
          return;
        }
        needsToggle.value = el.scrollHeight > lineHeight * 1.5;
      }
      else {
        // Collapsed : ellipse CSS active ssi le contenu déborde la
        // largeur visible.
        needsToggle.value = el.scrollWidth > el.clientWidth + 1;
      }
    }

    let observer: ResizeObserver | null = null;

    /**
     * Programme la mesure APRÈS le prochain paint du browser.
     * {@code nextTick} seul ne suffit pas : il fire après le DOM
     * update Vue mais avant la recomputation CSS du browser ;
     * {@code scrollWidth} peut alors refléter encore l'ancien état.
     * {@code requestAnimationFrame} garantit qu'on lit après layout
     * et paint complets , donc une mesure fiable du débordement.
     */
    function scheduleMeasure() {
      void nextTick(() => {
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => measureNeedsToggle());
        }
        else {
          measureNeedsToggle();
        }
      });
    }

    onMounted(() => {
      if (typeof ResizeObserver === "undefined") {
        scheduleMeasure();
        return;
      }
      observer = new ResizeObserver(() => measureNeedsToggle());
      if (contentEl.value) observer.observe(contentEl.value);
      scheduleMeasure();
    });

    onBeforeUnmount(() => {
      observer?.disconnect();
      observer = null;
    });

    watch(() => [props.text, props.expanded], scheduleMeasure);

    return {
      contentEl,
      needsToggle,
      isLong,
      toggleLabel,
      onToggle,
    };
  },
});
</script>

<style lang="scss" scoped>
/*
 * Stratégie de sizing en 2 modes :
 *
 *   - is-long ABSENT ( text.length ≤ maxChars ) :
 *     inline-block + max-width: var(--cell-cap). Le wrapper se size
 *     naturellement à son contenu , sans gaspillage. Pas de wrap
 *     forcé , pas d'icône ▾.
 *
 *   - is-long PRÉSENT ( text.length > maxChars ) :
 *     width: var(--cell-cap) FIXE ( pas seulement max-width ). La
 *     cellule occupe TOUJOURS exactement maxChars ch , peu importe
 *     l'état collapsed / expanded et peu importe l'état des cellules
 *     voisines. Cela résout :
 *       1. cellule courte = colonne tight ( pas de gaspillage )
 *       2. cellule longue = colonne plafonnée à maxChars ch
 *       3. la colonne ne se rétracte JAMAIS , même quand toutes les
 *          cellules sont expanded ( un width fixe verrouille
 *          l'intrinsic min/max ; sans lui , {@code overflow-wrap:
 *          anywhere} permet au browser de réduire l'intrinsic
 *          min-width à 1ch et de comprimer la colonne ).
 *       4. wrap point en expanded coïncide avec clip point en
 *          collapsed ( cf. mode déplié + word-break: break-all ).
 *
 * --cell-cap est piloté inline par la prop maxChars du composant ,
 * permettant à un parent de configurer la largeur cellule par cellule
 * sans toucher au CSS.
 */
.truncatable-cell {
  display: inline-block;
  position: relative;
  max-width: var(--cell-cap);
  padding-right: 1.5em;
  vertical-align: top;
}

.truncatable-cell.is-long {
  width: var(--cell-cap);
}

.truncatable-cell {

  &__content {
    /*
     * Mode REPLIÉ : 1 ligne stricte , ellipse CSS au niveau du
     * PARENT ( pas du <p>/<a> interne ). Important : sans ça , le
     * clip se produisait sur l'enfant et scrollWidth du parent ne
     * voyait pas l'overflow ⇒ ResizeObserver retournait false ⇒
     * pas de bouton ▾ sur les cellules pourtant ellipsées ( bug
     * images #353 #356 ).
     */
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /*
   * Override Bulma : on force <p class="column"> et
   * <a class="button inTable"> en display: INLINE pour qu'ils
   * participent au flux text du parent et héritent de son
   * white-space ( nowrap collapsed , normal expanded ). Plus
   * d'overflow / ellipsis au niveau de l'enfant - tout se gère sur
   * le parent .truncatable-cell__content.
   */
  &__content :deep(p),
  &__content :deep(a.button.inTable) {
    display: inline;
    margin: 0;
    padding: 0;
  }

  &__toggle {
    position: absolute;
    top: 0.15em;
    right: 0.15em;
    border: none;
    background: transparent;
    padding: 2px;
    cursor: pointer;
    color: #7a7a7a;
    z-index: 1;
    line-height: 1;

    &:hover {
      color: var(--theme-primary, #167df0);
    }
  }
}

/*
 * Mode DÉPLIÉ : on relâche les contraintes nowrap pour que le texte
 * wrap dans la largeur de la colonne ( table-layout: fixed garantit
 * que cette largeur reste stable entre replié et déplié ).
 * word-break + overflow-wrap garantissent que les très longs
 * fragments sans espace ( GeoJSON monoligne ) cassent au lieu de
 * pousser la colonne plus large.
 */
/*
 * Mode DÉPLIÉ : on applique les styles de wrap UNIQUEMENT aux
 * cellules longues ( .is-long ). Les courtes restent en layout
 * collapsed ( nowrap , inline-block , taille au contenu ) , même
 * si elles reçoivent la classe .is-expanded ( cas du Tier 3
 * "Tout déplier" qui force expanded=true sur toutes les cellules ).
 *
 * Pourquoi ? word-break: break-all + max-width sans width fixe
 * réduit l'intrinsic min-width à 1ch , ce qui permet à
 * table-layout: auto de comprimer la colonne. Conséquence visible
 * sur "Tout déplier" : les cellules courtes - qui n'auraient pas
 * dû bouger - se retrouvaient à wrapper inutilement parce que
 * leur colonne s'était rétractée.
 *
 * En limitant le wrap CSS à .is-long , les cellules courtes
 * conservent leur layout naturel ( nowrap + max-width ) qui ne
 * touche pas l'intrinsic sizing de la colonne.
 *
 *   - white-space: normal pour autoriser le wrap.
 *   - word-break: break-all pour wrap au CARACTÈRE ( pas au mot ).
 *     Combiné à width: var(--cell-cap) sur .is-long , chaque ligne
 *     contient EXACTEMENT le même nombre de chars qu'en collapsed
 *     ( où le clipping CSS coupe au bord du wrapper ). Donc la
 *     1ère ligne en déplié == la ligne tronquée en replié.
 */
.truncatable-cell.is-long.is-expanded {
  .truncatable-cell__content {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-break: break-all;
    overflow-wrap: anywhere;
  }

  .truncatable-cell__content :deep(p),
  .truncatable-cell__content :deep(a.button.inTable) {
    display: inline;
    max-width: 100%;
    white-space: normal;
    word-break: break-all;
    overflow-wrap: anywhere;
    overflow: visible;
    text-overflow: clip;
    height: auto;
    padding: 0;
    margin: 0;
    text-align: left;
  }
}
</style>
